// Role-based permissions configuration
// Based on the action numbering system defined in the requirements

export const ROLES = {
  ADMIN: 'admin',
  SUPERVISOR: 'supervisor',
  OFFICE_CLERK: 'office_clerk',
  DATA_ENTRY: 'data_entry',
  TECHNICIAN: 'technician',
  YARD_GOAT_OPERATOR: 'yard_goat_operator',
  DISPATCH: 'dispatch',
  DRIVER: 'driver',
  DIRECTOR: 'director',
  YARD_CHECKER: 'yard_checker',
  MASTER_ADMIN: 'master_admin'
};

// Full access roles (no restrictions)
const FULL_ACCESS_ROLES = [
  ROLES.ADMIN,
  ROLES.SUPERVISOR,
  ROLES.OFFICE_CLERK,
  ROLES.DATA_ENTRY,
  ROLES.MASTER_ADMIN,
  ROLES.DIRECTOR
];

// Permission definitions by feature area
export const PERMISSIONS = {
  // Assets Page
  ASSETS: {
    VIEW_LIST: 'assets.view_list',                    // 1
    SEARCH: 'assets.search',                          // 2
    FILTER_TYPE: 'assets.filter_type',                // 3
    CLICK_VEHICLE: 'assets.click_vehicle',            // 4
    ADD_VEHICLE: 'assets.add_vehicle',                // 5
    IMPORT_EXPORT: 'assets.import_export',            // 6
    REPORT_SERVICE: 'assets.report_service',          // 7
    SERVICE_REQUEST: 'assets.service_request',        // 8
    CREATE_DVIR: 'assets.create_dvir',                // 9
  },
  
  // Vehicle Detail Modal
  VEHICLE_DETAIL: {
    VIEW_DETAILS: 'vehicle.view_details',             // 1
    VIEW_HISTORY: 'vehicle.view_history',             // 2
    EDIT: 'vehicle.edit',                             // 3
    DELETE: 'vehicle.delete',                         // 4
    MARK_OOS: 'vehicle.mark_oos',                     // 5
    RETURN_TO_SERVICE: 'vehicle.return_to_service',   // 6
    RECORD_SERVICE: 'vehicle.record_service',         // 7
  },
  
  // DVIR Page
  DVIR: {
    VIEW_HISTORY: 'dvir.view_history',                // 1
    CREATE: 'dvir.create',                            // 2
    VIEW_REPORT: 'dvir.view_report',                  // 3
    DOWNLOAD_PDF: 'dvir.download_pdf',                // 4
    DELETE: 'dvir.delete',                            // 5
  },
  
  // Service Request Page
  SERVICE_REQUEST: {
    VIEW_ALL: 'service_request.view_all',             // 1
    CREATE: 'service_request.create',                 // 2
    MARK_VIEWED: 'service_request.mark_viewed',       // 3
    MARK_FULFILLED: 'service_request.mark_fulfilled', // 4
    REOPEN: 'service_request.reopen',                 // 5
    ARCHIVE: 'service_request.archive',               // 6
    DELETE: 'service_request.delete',                 // 7
    VIEW_IMAGES: 'service_request.view_images',       // 8
  },
  
  // Reports Page
  REPORTS: {
    DOWNLOAD_FLEET_SUMMARY: 'reports.fleet_summary',  // 1
    DOWNLOAD_SERVICE_HISTORY: 'reports.service_history', // 2
    DOWNLOAD_DVIR_AUDIT: 'reports.dvir_audit',        // 3
    SELECT_DRIVER: 'reports.select_driver',           // 4
    SELECT_PERIOD: 'reports.select_period',           // 5
  },
  
  // User Management Page
  USER_MANAGEMENT: {
    VIEW_USERS: 'users.view_users',                   // 1
    VIEW_REQUESTS: 'users.view_requests',             // 2
    SEARCH: 'users.search',                           // 3
    FILTER_ROLE: 'users.filter_role',                 // 4
    FILTER_STATUS: 'users.filter_status',             // 5
    ADD_USER: 'users.add_user',                       // 6
    EDIT_USER: 'users.edit_user',                     // 7
    RESET_PIN: 'users.reset_pin',                     // 8
    DEACTIVATE: 'users.deactivate',                   // 9
    ACTIVATE: 'users.activate',                       // 10
    APPROVE_REQUEST: 'users.approve_request',         // 11
    REJECT_REQUEST: 'users.reject_request',           // 12
    IMPORT_EXPORT: 'users.import_export',             // 13
  },
  
  // Settings Page
  SETTINGS: {
    VIEW_INTERVALS: 'settings.view_intervals',        // 1
    ADD_INTERVAL: 'settings.add_interval',            // 2
    EDIT_INTERVAL: 'settings.edit_interval',          // 3
    DELETE_INTERVAL: 'settings.delete_interval',      // 4
  },
  
  // Navigation
  NAV: {
    ASSETS: 'nav.assets',
    DVIR: 'nav.dvir',
    SERVICE_REQUEST: 'nav.service_request',
    PARTS: 'nav.parts',
    REPORTS: 'nav.reports',
    USERS: 'nav.users',
    SETTINGS: 'nav.settings',
  }
};

// Role permissions mapping
const ROLE_PERMISSIONS = {
  // Admin - Full access
  [ROLES.ADMIN]: '*',
  
  // Supervisor - Full access (same as Admin)
  [ROLES.SUPERVISOR]: '*',
  
  // Office Clerk - Full access
  [ROLES.OFFICE_CLERK]: '*',
  
  // Data Entry - Full access
  [ROLES.DATA_ENTRY]: '*',
  
  // Master Admin - Full access
  [ROLES.MASTER_ADMIN]: '*',
  
  // Director - Full access
  [ROLES.DIRECTOR]: '*',
  
  // Technician - Limited access
  [ROLES.TECHNICIAN]: [
    // Assets: 1-4, 7-8
    PERMISSIONS.ASSETS.VIEW_LIST,
    PERMISSIONS.ASSETS.SEARCH,
    PERMISSIONS.ASSETS.FILTER_TYPE,
    PERMISSIONS.ASSETS.CLICK_VEHICLE,
    PERMISSIONS.ASSETS.REPORT_SERVICE,
    PERMISSIONS.ASSETS.SERVICE_REQUEST,
    // Vehicle Detail: 1-2, 5-7 (view, OOS controls, record service)
    PERMISSIONS.VEHICLE_DETAIL.VIEW_DETAILS,
    PERMISSIONS.VEHICLE_DETAIL.VIEW_HISTORY,
    PERMISSIONS.VEHICLE_DETAIL.MARK_OOS,
    PERMISSIONS.VEHICLE_DETAIL.RETURN_TO_SERVICE,
    PERMISSIONS.VEHICLE_DETAIL.RECORD_SERVICE,
    // Service Request: 1-5, 8
    PERMISSIONS.SERVICE_REQUEST.VIEW_ALL,
    PERMISSIONS.SERVICE_REQUEST.CREATE,
    PERMISSIONS.SERVICE_REQUEST.MARK_VIEWED,
    PERMISSIONS.SERVICE_REQUEST.MARK_FULFILLED,
    PERMISSIONS.SERVICE_REQUEST.REOPEN,
    PERMISSIONS.SERVICE_REQUEST.VIEW_IMAGES,
    // Reports: 1-5 (full access)
    PERMISSIONS.REPORTS.DOWNLOAD_FLEET_SUMMARY,
    PERMISSIONS.REPORTS.DOWNLOAD_SERVICE_HISTORY,
    PERMISSIONS.REPORTS.DOWNLOAD_DVIR_AUDIT,
    PERMISSIONS.REPORTS.SELECT_DRIVER,
    PERMISSIONS.REPORTS.SELECT_PERIOD,
    // Navigation
    PERMISSIONS.NAV.ASSETS,
    PERMISSIONS.NAV.SERVICE_REQUEST,
    PERMISSIONS.NAV.PARTS,
    PERMISSIONS.NAV.REPORTS,
  ],
  
  // Yard-Goat Operator - Very limited access
  [ROLES.YARD_GOAT_OPERATOR]: [
    // Assets: 1-4
    PERMISSIONS.ASSETS.VIEW_LIST,
    PERMISSIONS.ASSETS.SEARCH,
    PERMISSIONS.ASSETS.FILTER_TYPE,
    PERMISSIONS.ASSETS.CLICK_VEHICLE,
    // Vehicle Detail: 1-2
    PERMISSIONS.VEHICLE_DETAIL.VIEW_DETAILS,
    PERMISSIONS.VEHICLE_DETAIL.VIEW_HISTORY,
    // Navigation
    PERMISSIONS.NAV.ASSETS,
  ],
  
  // Yard Checker - Same as Yard-Goat Operator
  [ROLES.YARD_CHECKER]: [
    PERMISSIONS.ASSETS.VIEW_LIST,
    PERMISSIONS.ASSETS.SEARCH,
    PERMISSIONS.ASSETS.FILTER_TYPE,
    PERMISSIONS.ASSETS.CLICK_VEHICLE,
    PERMISSIONS.VEHICLE_DETAIL.VIEW_DETAILS,
    PERMISSIONS.VEHICLE_DETAIL.VIEW_HISTORY,
    PERMISSIONS.NAV.ASSETS,
  ],
  
  // Dispatch - View-focused access
  [ROLES.DISPATCH]: [
    // Assets: 1-4
    PERMISSIONS.ASSETS.VIEW_LIST,
    PERMISSIONS.ASSETS.SEARCH,
    PERMISSIONS.ASSETS.FILTER_TYPE,
    PERMISSIONS.ASSETS.CLICK_VEHICLE,
    // Vehicle Detail: 1-2
    PERMISSIONS.VEHICLE_DETAIL.VIEW_DETAILS,
    PERMISSIONS.VEHICLE_DETAIL.VIEW_HISTORY,
    // DVIR: 1, 3-4 (view and download only)
    PERMISSIONS.DVIR.VIEW_HISTORY,
    PERMISSIONS.DVIR.VIEW_REPORT,
    PERMISSIONS.DVIR.DOWNLOAD_PDF,
    // Reports: 1-5
    PERMISSIONS.REPORTS.DOWNLOAD_FLEET_SUMMARY,
    PERMISSIONS.REPORTS.DOWNLOAD_SERVICE_HISTORY,
    PERMISSIONS.REPORTS.DOWNLOAD_DVIR_AUDIT,
    PERMISSIONS.REPORTS.SELECT_DRIVER,
    PERMISSIONS.REPORTS.SELECT_PERIOD,
    // Navigation
    PERMISSIONS.NAV.ASSETS,
    PERMISSIONS.NAV.DVIR,
    PERMISSIONS.NAV.REPORTS,
  ],
  
  // Driver - Basic operational access
  [ROLES.DRIVER]: [
    // Assets: 1-4 + shortcuts to service/DVIR
    PERMISSIONS.ASSETS.VIEW_LIST,
    PERMISSIONS.ASSETS.SEARCH,
    PERMISSIONS.ASSETS.FILTER_TYPE,
    PERMISSIONS.ASSETS.CLICK_VEHICLE,
    PERMISSIONS.ASSETS.REPORT_SERVICE,
    PERMISSIONS.ASSETS.SERVICE_REQUEST,
    PERMISSIONS.ASSETS.CREATE_DVIR,
    // Vehicle Detail: 1-2, 7 (view + record service)
    PERMISSIONS.VEHICLE_DETAIL.VIEW_DETAILS,
    PERMISSIONS.VEHICLE_DETAIL.VIEW_HISTORY,
    PERMISSIONS.VEHICLE_DETAIL.RECORD_SERVICE,
    // DVIR: 1-4 (can create and view, but not delete)
    PERMISSIONS.DVIR.VIEW_HISTORY,
    PERMISSIONS.DVIR.CREATE,
    PERMISSIONS.DVIR.VIEW_REPORT,
    PERMISSIONS.DVIR.DOWNLOAD_PDF,
    // Service Request: 1-2 (can view and create only)
    PERMISSIONS.SERVICE_REQUEST.VIEW_ALL,
    PERMISSIONS.SERVICE_REQUEST.CREATE,
    PERMISSIONS.SERVICE_REQUEST.VIEW_IMAGES,
    // Navigation - NO Parts access for drivers
    PERMISSIONS.NAV.ASSETS,
    PERMISSIONS.NAV.DVIR,
    PERMISSIONS.NAV.SERVICE_REQUEST,
  ],
};

/**
 * Check if a user role has a specific permission
 * @param {string} role - User's role
 * @param {string} permission - Permission to check
 * @returns {boolean}
 */
export const hasPermission = (role, permission) => {
  if (!role) return false;
  
  const rolePerms = ROLE_PERMISSIONS[role.toLowerCase()];
  
  // Full access
  if (rolePerms === '*') return true;
  
  // Check specific permission
  if (Array.isArray(rolePerms)) {
    return rolePerms.includes(permission);
  }
  
  return false;
};

/**
 * Check if a user role has any of the specified permissions
 * @param {string} role - User's role
 * @param {string[]} permissions - Permissions to check
 * @returns {boolean}
 */
export const hasAnyPermission = (role, permissions) => {
  return permissions.some(perm => hasPermission(role, perm));
};

/**
 * Check if a user role has all of the specified permissions
 * @param {string} role - User's role
 * @param {string[]} permissions - Permissions to check
 * @returns {boolean}
 */
export const hasAllPermissions = (role, permissions) => {
  return permissions.every(perm => hasPermission(role, perm));
};

/**
 * Check if user can access a navigation item
 * @param {string} role - User's role
 * @param {string} navItem - Navigation item ID
 * @returns {boolean}
 */
export const canAccessNav = (role, navItem) => {
  const navPermMap = {
    'assets': PERMISSIONS.NAV.ASSETS,
    'dvir': PERMISSIONS.NAV.DVIR,
    'service-request': PERMISSIONS.NAV.SERVICE_REQUEST,
    'parts': PERMISSIONS.NAV.PARTS,
    'reports': PERMISSIONS.NAV.REPORTS,
    'users': PERMISSIONS.NAV.USERS,
    'settings': PERMISSIONS.NAV.SETTINGS,
  };
  
  const permission = navPermMap[navItem];
  if (!permission) return true; // Unknown nav items are accessible by default
  
  return hasPermission(role, permission);
};

/**
 * Get all permissions for a role
 * @param {string} role - User's role
 * @returns {string[]|'*'}
 */
export const getRolePermissions = (role) => {
  return ROLE_PERMISSIONS[role?.toLowerCase()] || [];
};

/**
 * Check if role has full access
 * @param {string} role - User's role
 * @returns {boolean}
 */
export const hasFullAccess = (role) => {
  return ROLE_PERMISSIONS[role?.toLowerCase()] === '*';
};

// ==================== PARTS & INVENTORY PERMISSION TIERS ====================
// Spec tiers: viewer < mechanic < parts_manager < admin
export const PARTS_TIERS = {
  ADMIN: 'admin',
  PARTS_MANAGER: 'parts_manager',
  MECHANIC: 'mechanic',
  VIEWER: 'viewer',
};

const ROLE_TO_PARTS_TIER = {
  master_admin: 'admin',
  admin: 'admin',
  supervisor: 'parts_manager',
  director: 'parts_manager',
  office_clerk: 'parts_manager',
  data_entry: 'parts_manager',
  technician: 'mechanic',
  driver: 'mechanic',
  dispatch: 'viewer',
  yard_goat_operator: 'viewer',
  yard_checker: 'viewer',
};

/**
 * Resolve a user's parts permission tier.
 * Accepts a user object (preferred, may carry an explicit parts_role) or a role string.
 */
export const getPartsRole = (userOrRole) => {
  if (!userOrRole) return 'viewer';
  if (typeof userOrRole === 'string') {
    return ROLE_TO_PARTS_TIER[userOrRole.toLowerCase()] || 'viewer';
  }
  const explicit = (userOrRole.parts_role || '').toLowerCase();
  if (['admin', 'parts_manager', 'mechanic', 'viewer'].includes(explicit)) return explicit;
  return ROLE_TO_PARTS_TIER[(userOrRole.role || '').toLowerCase()] || 'viewer';
};

export const PARTS_TIER_LABELS = {
  admin: 'Admin',
  parts_manager: 'Parts Manager',
  mechanic: 'Mechanic',
  viewer: 'Viewer',
};

/** Can manage inventory master, vendors, locations, POs, receiving, cores, adjustments, etc. */
export const canManageParts = (userOrRole) => ['admin', 'parts_manager'].includes(getPartsRole(userOrRole));

/** Can create parts requests (mechanics and above). */
export const canCreatePartsRequest = (userOrRole) => ['admin', 'parts_manager', 'mechanic'].includes(getPartsRole(userOrRole));

export default {
  ROLES,
  PERMISSIONS,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canAccessNav,
  getRolePermissions,
  hasFullAccess,
  getPartsRole,
  canManageParts,
  canCreatePartsRequest,
};
